import React from 'react';
import { Button, Label, Popover } from '@patternfly/react-core';
import { OutlinedQuestionCircleIcon } from '@patternfly/react-icons';
import type { BackupAttributionSuggestion, BackupSuggestionStatus } from '../../../types/models/BackupVmMapping';

/**
 * Rendering NetBox's attribution proposal HONESTLY — the heart of the backup attribution screen.
 *
 * `suggestion.status` says *how* a proposal was reached, and the weak ways must never look like
 * the strong one. The Manager Agent stamps every VM it discovers with its **hypervisor's** owner,
 * so on shared hardware NetBox names the house tenant for exactly the customer VMs this screen
 * exists to re-attribute — a "confident"-looking weak match would move a paying customer's backups
 * into the house tenant.
 *
 * The statuses are the whole vocabulary: deliberately NO numeric confidence score is invented on
 * top of them, and there is no bulk-apply anywhere in this feature.
 */

/** PatternFly `Label` colors, narrowed to the palette this module uses. */
type SuggestionLabelColor = 'green' | 'orange' | 'red' | 'grey';

export interface SuggestionStatusDescriptor {
  /** The visible label text. Distinct per status: colour is never the only signal. */
  text: string;
  color: SuggestionLabelColor;
  /** Outlined = the same finding with a caveat (retired directory), a step down from filled. */
  isOutline: boolean;
  /** One sentence a staff member can act on, shown as the label's tooltip/popover body. */
  hint: string;
}

const DESCRIPTORS: Record<BackupSuggestionStatus, SuggestionStatusDescriptor | null> = {
  pinned: {
    text: 'Confident',
    color: 'green',
    isOutline: false,
    hint: 'Exactly one NetBox VM of this name is pinned to this hypervisor.',
  },
  pinned_retired: {
    text: 'Confident (retired)',
    color: 'green',
    isOutline: true,
    hint: 'Matched on this hypervisor after ignoring the retired directory’s .old suffix.',
  },
  name_only: {
    text: 'Name match only',
    color: 'orange',
    isOutline: false,
    hint: 'The name matches one NetBox VM, but that VM is not pinned to any hypervisor. Verify before applying.',
  },
  name_only_retired: {
    text: 'Name match only (retired)',
    color: 'orange',
    isOutline: true,
    hint: 'A name-only match, reached after ignoring the retired directory’s .old suffix. Verify before applying.',
  },
  ambiguous_owner: {
    text: 'Ambiguous',
    color: 'red',
    isOutline: false,
    hint: 'Several NetBox VMs match this name with different owners, so NetBox refuses to guess.',
  },
  owner_unmapped: {
    text: 'Unknown owner',
    color: 'grey',
    isOutline: false,
    hint: 'Matched a NetBox VM, but no Manager client carries that NetBox owner name.',
  },
  owner_ambiguous: {
    text: 'Owner conflict',
    color: 'red',
    isOutline: false,
    hint: 'Two Manager clients carry that NetBox owner name — fix the client data before attributing.',
  },
  no_owner: {
    text: 'No owner',
    color: 'grey',
    isOutline: false,
    hint: 'The matching NetBox VM has no owner set.',
  },
  no_match: {
    text: 'No NetBox match',
    color: 'grey',
    isOutline: false,
    hint: 'No NetBox VM carries this name — a renamed VM, or a typo in the directory.',
  },
  // Rendered as nothing: the page-level banner already explains the outage, and a per-row label
  // would just repeat it 49 times.
  netbox_unavailable: null,
};

/**
 * The presentation of one suggestion status, or `null` when the status must render nothing.
 *
 * An UNKNOWN status (a value a future backend adds) degrades to a neutral grey label showing the
 * raw string — surfacing the new status rather than crashing the table or, worse, silently
 * dropping the row's only warning.
 */
export function suggestionStatusDescriptor(status: string): SuggestionStatusDescriptor | null {
  if (Object.prototype.hasOwnProperty.call(DESCRIPTORS, status)) {
    return DESCRIPTORS[status as BackupSuggestionStatus];
  }
  return {
    text: status,
    color: 'grey',
    isOutline: false,
    hint: 'This suggestion status is not known to this version of the Manager. Attribute manually.',
  };
}

/**
 * `true` when the suggestion actually names a Manager client. This — never the status string — is
 * what gates the one-click "Attribute to X" action: every non-proposing status carries
 * `clientID: null`.
 */
export function suggestionProposesClient(suggestion: BackupAttributionSuggestion): boolean {
  return suggestion.clientID !== null;
}

/** A weak (name-only) match needs an explicit caveat next to it, not just a different colour. */
export function isNameOnlyMatch(status: string): boolean {
  return status === 'name_only' || status === 'name_only_retired';
}

interface SuggestionStatusLabelProps {
  suggestion: BackupAttributionSuggestion;
  /** The subject's hypervisor, named in the name-only caveat. */
  hypervisor: string;
}

/**
 * The status label itself, with a popover carrying the caveat for weak matches.
 *
 * The popover trigger is a real `Button` because a tooltip on a static element is not reliably
 * announced by assistive technologies (PatternFly's own label accessibility guidance).
 */
export function SuggestionStatusLabel({ suggestion, hypervisor }: SuggestionStatusLabelProps) {
  const descriptor = suggestionStatusDescriptor(suggestion.status);
  if (descriptor === null) {
    return null;
  }

  const label = (
    <Label color={descriptor.color} variant={descriptor.isOutline ? 'outline' : 'filled'} isCompact>
      {descriptor.text}
    </Label>
  );

  if (!isNameOnlyMatch(suggestion.status)) {
    return label;
  }

  return (
    <>
      {label}{' '}
      <Popover
        aria-label="Why this match is weak"
        headerContent="Name match only"
        bodyContent={
          <>
            This VM is not pinned to <strong>{hypervisor}</strong> in NetBox — the match is by name alone, and a
            tenant can rename a VM to any string. Check it is the same machine before attributing.
          </>
        }
      >
        <Button
          variant="plain"
          isInline
          aria-label={`Why the suggestion for ${suggestion.matched_name ?? 'this VM'} is a name match only`}
          icon={<OutlinedQuestionCircleIcon />}
        />
      </Popover>
    </>
  );
}
